// Modules
import { Store } from '@ngrx/store';
import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { HttpInterceptor, HttpEvent, HttpHandler, HttpRequest } from '@angular/common/http';

// Rxjs
import { Observable, throwError } from 'rxjs';
import { take, catchError, exhaustMap } from 'rxjs/operators';

// Services
import { AuthService } from '@services/auth-service/auth.service';
import { AppConfigService } from '@shared-module/services/app-config/app-config.service';
import { SbiToastService } from '@shared-module/services/sbi-toast/sbi-toast.service';
import { MsalService } from '@azure/msal-angular';

// Store - Reducer
import { AppState } from '@store/reducers';
// Store - Actions
import * as AuthActions from '@store/actions/auth.actions';

@Injectable()
export class AuthInterceptorService implements HttpInterceptor {
  // Constructor
  constructor(
    private _app: AppConfigService,
    private _store: Store<AppState>,
    private _authService: AuthService,
    private readonly _sbiToast: SbiToastService,
    private _router: Router,
    private _matDialog$: MatDialog,
    private _authMsalService: MsalService
  ) {}

  // Intercept
  intercept(
    req: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    // Do not intercept public auth requests
    if (req.url.indexOf('/auth') !== -1) {
      const url = req.url;

      // Logout, Refresh Token, Get Profile and Change Password methods, requires authorization header
      if (
        url.indexOf('/logout') === -1 &&
        url.indexOf('/refreshtoken') === -1 &&
        url.indexOf('/changepassword') === -1 &&
        url.indexOf('/profile') === -1
      ) {
        return next.handle(req);
      }
    }

    return this._store.pipe(
      take(1),
      exhaustMap(() => {
        const loggedUser = JSON.parse(
          sessionStorage.getItem(this._app.loggedUserSessionKey)
        );
        if (loggedUser)
          req = this._addAuthenticationToken(req, loggedUser.accessToken);

        return next.handle(req).pipe(
          catchError((err: any) => {
            if (err.status === 401) {
              if (req.url.includes('/auth/refreshtoken') !== true) {
                // Generate params for token refreshing
                return this._authService
                  .refreshToken(loggedUser.accessToken, loggedUser.refreshToken)
                  .pipe(
                    exhaustMap((response: any) => {
                      // If reload successful update tokens
                      if (response.status === 200) {
                        const accessToken = response.body.accessToken.token;
                        const refreshToken = response.body.refreshToken;

                        // Update tokens
                        const updatedLoggedUser = {
                          ...loggedUser,
                          accessToken,
                          refreshToken,
                        };
                        sessionStorage.setItem(
                          this._app.loggedUserSessionKey,
                          JSON.stringify(updatedLoggedUser)
                        );
                        this._store.dispatch(
                          AuthActions.updateUserTokens({
                            payload: { accessToken, refreshToken },
                          })
                        );

                        // Clone our fieled request ant try to resend it
                        req = this._addAuthenticationToken(req, accessToken);
                        return next.handle(req);
                      }
                    }),
                    catchError(() => {
                      this._sbiToast.error(
                        'Sesión expirada. Por favor vuelva a ingresar al sistema.'
                      );

                      this._store.dispatch(AuthActions.logoutSuccessful());

                      // Clear information
                      sessionStorage.clear();

                      // Close all dialogs
                      this._matDialog$.closeAll();

                      // Microsoft logout and get back to login screen
                      const currentAccount =
                        this._authMsalService.instance.getAllAccounts()[0];
                      const logoutHint =
                        currentAccount.idTokenClaims.login_hint;
                      this._authMsalService.logoutPopup({
                        mainWindowRedirectUri:
                          this._app.microsoft365.redirectUri,
                        logoutHint: logoutHint,
                      });

                      return throwError(err);
                    })
                  );
              }
            }
            return throwError(err);
          })
        );
      })
    );
  }

  // Private method to inject the token
  private _addAuthenticationToken(
    req: HttpRequest<any>,
    accessToken: string
  ): HttpRequest<any> {
    return req.clone({
      setHeaders: { Authorization: `Bearer ${accessToken}` },
    });
  }
}
